import { Component } from '@eppendorf/vnls-device-service-utils';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { Threshold } from '@eppendorf/vnls-react-components-preview';
import i18next from 'i18next';

import { hasThresholdValue } from '$features/monitoring/monitoring.utils.snapshot';
import { ThresholdDynamicValueLabelSnapshot } from '$features/monitoring/parameters/snapshot/thresholds-form-snapshot/threshold-dynamic-value-label-snapshot';
import { ThresholdStaticValueLabelSnapshot } from '$features/monitoring/parameters/snapshot/thresholds-form-snapshot/threshold-static-value-label-snapshot';
import { AlertThresholdKey, ThresholdType } from '$features/monitoring/parameters/types';
import {
  createValueElement,
  applyFractionDigits,
} from '$features/monitoring/shared/formatting.utils';

export function createThresholdTile(type: ThresholdType) {
  const bgColorClass = type === ThresholdType.Alert ? 'bg-red-500' : 'bg-orange-400';

  return (
    <div className={`${bgColorClass} flex flex__jc--center flex__ai--center`}>
      <Icon name={`event-${type}`} size={IconSizeClasses.Small} />
    </div>
  );
}

export function createThresholdTileOk() {
  return (
    <div className="bg-green-500 flex flex__jc--center flex__ai--center">
      <Icon name="checkmark" size={IconSizeClasses.Small} className="bg-white" />
    </div>
  );
}

const thresholdFactory = (
  alertStateKeyOrSo: AlertThresholdKey,
  type: ThresholdType,
  shadowComp?: Component,
): Threshold | undefined =>
  shadowComp
    ? {
        descriptionLabel: i18next.t(`alertThresholds.${alertStateKeyOrSo ?? 'unknown'}`),
        component: createThresholdTile(type),
      }
    : undefined;

function getEffectiveThreshold(
  value: number | undefined,
  enabled: boolean,
  isSensor?: boolean,
): string | undefined {
  if (!enabled) {
    return undefined;
  }

  if (hasThresholdValue(value)) {
    return `${value}`;
  }

  return isSensor ? '–' : undefined;
}

export const getThresholdSetpointsFactorySnapshot =
  (shadowComp?: Component, isSensor?: boolean): ((editable: boolean) => Threshold[]) =>
  (isEditing: boolean) => {
    if (!shadowComp) {
      return [];
    }

    const alertHighEnabled = shadowComp.alertThresholdEnabled?.highEnabled !== false;
    const alertLowEnabled = shadowComp.alertThresholdEnabled?.lowEnabled !== false;
    const warnHighEnabled = shadowComp.warnThresholdEnabled?.highEnabled !== false;
    const warnLowEnabled = shadowComp.warnThresholdEnabled?.lowEnabled !== false;

    const effectiveTresholds = {
      alertHigh: getEffectiveThreshold(
        shadowComp.alertThreshold?.high,
        alertHighEnabled,
        isSensor,
      ),
      alertLow: getEffectiveThreshold(
        shadowComp.alertThreshold?.low,
        alertLowEnabled,
        isSensor,
      ),
      warnHigh: getEffectiveThreshold(
        shadowComp.warnThreshold?.high,
        warnHighEnabled,
        isSensor,
      ),
      warnLow: getEffectiveThreshold(
        shadowComp.warnThreshold?.low,
        warnLowEnabled,
        isSensor,
      ),
    };

    return [
      effectiveTresholds.alertHigh !== undefined
        ? {
            ...thresholdFactory('alertHigh', ThresholdType.Alert, shadowComp),
            valueLabel: isEditing ? (
              <ThresholdDynamicValueLabelSnapshot
                alertThreshKey="alertHigh"
                shadowComp={shadowComp}
              />
            ) : (
              <ThresholdStaticValueLabelSnapshot
                shadowComp={shadowComp}
                threshValue={effectiveTresholds.alertHigh}
              />
            ),
          }
        : undefined,
      effectiveTresholds.warnHigh !== undefined
        ? {
            ...thresholdFactory('warnHigh', ThresholdType.Warn, shadowComp),
            valueLabel: isEditing ? (
              <ThresholdDynamicValueLabelSnapshot
                alertThreshKey="warnHigh"
                shadowComp={shadowComp}
              />
            ) : (
              <ThresholdStaticValueLabelSnapshot
                shadowComp={shadowComp}
                threshValue={effectiveTresholds.warnHigh}
              />
            ),
          }
        : undefined,

      shadowComp?.setPoint && {
        component: createThresholdTileOk(),
      },

      shadowComp?.setPoint
        ? {
            descriptionLabel: 'Set point',
            valueLabel: `${createValueElement(
              applyFractionDigits(shadowComp.fractionalDigits),
              shadowComp.valueType,
              shadowComp.setPoint,
            )} ${shadowComp.unit}`,
            component: (
              <div className="flex flex__jc--center flex__ai--center h-4xs p-bottom-xl bg-gray-800 color-white" />
            ),
            isReference: true,
          }
        : {
            component: createThresholdTileOk(),
            isReference: true,
          },

      shadowComp?.setPoint && {
        component: createThresholdTileOk(),
      },

      effectiveTresholds.warnLow !== undefined
        ? {
            ...thresholdFactory('warnLow', ThresholdType.Warn, shadowComp),
            valueLabel: isEditing ? (
              <ThresholdDynamicValueLabelSnapshot
                alertThreshKey="warnLow"
                shadowComp={shadowComp}
              />
            ) : (
              <ThresholdStaticValueLabelSnapshot
                shadowComp={shadowComp}
                threshValue={effectiveTresholds.warnLow}
              />
            ),
          }
        : undefined,
      effectiveTresholds.alertLow !== undefined
        ? {
            ...thresholdFactory('alertLow', ThresholdType.Alert, shadowComp),
            valueLabel: isEditing ? (
              <ThresholdDynamicValueLabelSnapshot
                alertThreshKey="alertLow"
                shadowComp={shadowComp}
              />
            ) : (
              <ThresholdStaticValueLabelSnapshot
                shadowComp={shadowComp}
                threshValue={effectiveTresholds.alertLow}
              />
            ),
          }
        : undefined,
    ].filter(Boolean) as Threshold[];
  };
